import React from 'react'
import { Badge } from '../ui/Badge'
import { formatCurrency } from '../../lib/orders-utils'
import { useThemeTokens } from '../../themes'
import type { OrderItem } from '../../types/orders'
import { getAllProducts, type EsscoProduct } from '../../data'
import { useSmartQuantityInput } from '../../hooks/useSmartQuantityInput'

export type OrderItemRowMode = 'table' | 'grid' | 'compact' | 'summary' | 'editable'

interface OrderItemRowProps {
  item: OrderItem
  mode: OrderItemRowMode
  showQuantity?: boolean
  showPricing?: boolean
  showTotal?: boolean
  className?: string
  style?: React.CSSProperties
  // For editable mode
  onQuantityChange?: (itemId: string, quantity: number) => void
  onRemoveItem?: (itemId: string) => void
  setQuantityInputRef?: (itemId: string, ref: HTMLInputElement | null) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

export const OrderItemRow: React.FC<OrderItemRowProps> = ({
  item,
  mode,
  showQuantity = true,
  showPricing = true,
  showTotal = true,
  className = '',
  style = {},
  onQuantityChange,
  onRemoveItem,
  setQuantityInputRef,
  onKeyDown
}) => {
  const theme = useThemeTokens()
  const enhancedProducts = getAllProducts()
  
  // Smart quantity input hook for editable mode
  const quantityInput = useSmartQuantityInput(
    item.quantity,
    (newValue: number) => onQuantityChange?.(item.id, newValue)
  )
  
  // Find enhanced product data for additional info like images
  const enhancedProduct = enhancedProducts.find((p: EsscoProduct) => p.id === item.catalogItem.id)
  const hasImage = enhancedProduct?.imageUrl
  const isLegacyProduct = enhancedProduct?.isLegacyProduct

  // Image component
  const ProductImage: React.FC<{ size: number }> = ({ size }) => {
    if (!hasImage) {
      return (
        <div
          style={{
            width: size,
            height: size,
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: theme.colors.textSecondary,
            flexShrink: 0
          }}
        >
          No Image
        </div>
      )
    }

    return (
      <img
        src={enhancedProduct.imageUrl}
        alt={item.catalogItem.name}
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: theme.borderRadius.sm,
          border: `1px solid ${theme.colors.border}`,
          flexShrink: 0
        }}
        onError={(e) => {
          const target = e.target as HTMLImageElement
          target.style.display = 'none'
          const parent = target.parentElement
          if (parent) {
            parent.innerHTML = `
              <div style="
                width: ${size}px;
                height: ${size}px;
                background-color: ${theme.colors.background};
                border: 1px solid ${theme.colors.border};
                border-radius: ${theme.borderRadius.sm};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                color: ${theme.colors.textSecondary};
                flex-shrink: 0;
              ">
                No Image
              </div>
            `
          }
        }}
      />
    )
  }

  // Table row mode (for ViewOrder)
  if (mode === 'table') {
    const tableCellStyles: React.CSSProperties = {
      borderBottom: `1px solid ${theme.colors.border}`,
      padding: theme.spacing.sm,
      fontSize: theme.typography.fontSize.sm
    }

    return (
      <tr className={className} style={style}>
        {/* Image */}
        <td style={{ ...tableCellStyles, textAlign: 'center', width: '60px' }}>
          <ProductImage size={50} />
        </td>
        
        {/* Quantity */}
        {showQuantity && (
          <td style={{ ...tableCellStyles, textAlign: 'center', fontWeight: theme.typography.fontWeight.bold, width: '60px' }}>
            {item.quantity}
          </td>
        )}
        
        {/* Item Description */}
        <td style={tableCellStyles}>
          <div style={{ fontWeight: theme.typography.fontWeight.medium, marginBottom: '2px' }}>
            {item.catalogItem.name}
            {isLegacyProduct && (
              <Badge 
                variant="warning" 
                style={{ marginLeft: theme.spacing.xs, fontSize: '10px' }}
              >
                Legacy
              </Badge>
            )}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: theme.colors.textSecondary,
            lineHeight: '1.4'
          }}>
            {item.catalogItem.description}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: theme.colors.textSecondary,
            marginTop: '4px'
          }}>
            ID: {item.catalogItem.id}
          </div>
        </td>
        
        {/* Unit */}
        <td style={{ ...tableCellStyles, textAlign: 'center', width: '80px' }}>
          {item.catalogItem.unit}
        </td>
        
        {/* Qty/Unit */}
        <td style={{ ...tableCellStyles, textAlign: 'center', width: '100px' }}>
          {item.catalogItem.qtyPerUnit}
        </td>
        
        {/* Unit Price */}
        {showPricing && (
          <td style={{ ...tableCellStyles, textAlign: 'right', width: '120px' }}>
            <div>{formatCurrency(item.catalogItem.pricePerUnit)}</div>
            <div style={{ 
              fontSize: '12px', 
              color: theme.colors.textSecondary 
            }}>
              ({formatCurrency(item.catalogItem.perItem)}/item)
            </div>
          </td>
        )}
        
        {/* Total Price */}
        {showTotal && (
          <td style={{ 
            ...tableCellStyles, 
            textAlign: 'right',
            fontWeight: theme.typography.fontWeight.bold,
            width: '120px'
          }}>
            {formatCurrency(item.totalPrice)}
          </td>
        )}
      </tr>
    )
  }

  // Grid mode (for CreateOrder)
  if (mode === 'grid') {
    return (
      <div
        className={className}
        style={{
          display: 'grid',
          gridTemplateColumns: '50px 1fr 80px 80px 100px 100px auto',
          gap: theme.spacing.sm,
          alignItems: 'center',
          padding: theme.spacing.sm,
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.md,
          fontSize: theme.typography.fontSize.sm,
          ...style
        }}
      >
        {/* Image */}
        <ProductImage size={50} />
        
        {/* Item Info */}
        <div style={{ minWidth: 0 }}>
          <div style={{ 
            fontWeight: theme.typography.fontWeight.medium, 
            marginBottom: '2px',
            wordBreak: 'break-word'
          }}>
            {item.catalogItem.name}
            {isLegacyProduct && (
              <Badge 
                variant="warning" 
                style={{ marginLeft: theme.spacing.xs, fontSize: '10px' }}
              >
                Legacy
              </Badge>
            )}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: theme.colors.textSecondary,
            lineHeight: '1.3'
          }}>
            {item.catalogItem.description}
          </div>
        </div>
        
        {/* Quantity */}
        {showQuantity && (
          <div style={{ 
            textAlign: 'center', 
            fontWeight: theme.typography.fontWeight.bold 
          }}>
            {item.quantity}
          </div>
        )}
        
        {/* Unit */}
        <div style={{ textAlign: 'center' }}>
          {item.catalogItem.unit}
        </div>
        
        {/* Qty/Unit */}
        <div style={{ textAlign: 'center' }}>
          {item.catalogItem.qtyPerUnit}
        </div>
        
        {/* Unit Price */}
        {showPricing && (
          <div style={{ textAlign: 'right' }}>
            <div>{formatCurrency(item.catalogItem.pricePerUnit)}</div>
            <div style={{ 
              fontSize: '11px', 
              color: theme.colors.textSecondary 
            }}>
              {formatCurrency(item.catalogItem.perItem)}/ea
            </div>
          </div>
        )}
        
        {/* Total */}
        {showTotal && (
          <div style={{ 
            textAlign: 'right',
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.success
          }}>
            {formatCurrency(item.totalPrice)}
          </div>
        )}
      </div>
    )
  }

  // Compact mode (for OrderForm)
  if (mode === 'compact') {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm,
          padding: theme.spacing.sm,
          backgroundColor: theme.colors.background,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.sm,
          fontSize: theme.typography.fontSize.sm,
          ...style
        }}
      >
        {/* Image */}
        <ProductImage size={40} />
        
        {/* Item Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontWeight: theme.typography.fontWeight.medium,
            marginBottom: '1px'
          }}>
            {item.catalogItem.name}
            {isLegacyProduct && (
              <Badge 
                variant="warning" 
                style={{ marginLeft: theme.spacing.xs, fontSize: '9px' }}
              >
                Legacy
              </Badge>
            )}
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: theme.colors.textSecondary 
          }}>
            {item.catalogItem.description}
          </div>
        </div>
        
        {/* Quantity & Unit */}
        {showQuantity && (
          <div style={{ 
            textAlign: 'center',
            fontSize: theme.typography.fontSize.sm,
            minWidth: '60px'
          }}>
            <div style={{ fontWeight: theme.typography.fontWeight.bold }}>
              {item.quantity}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: theme.colors.textSecondary 
            }}>
              {item.catalogItem.unit}
            </div>
          </div>
        )}
        
        {/* Price */}
        {showTotal && (
          <div style={{ 
            textAlign: 'right',
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.success,
            minWidth: '80px'
          }}>
            {formatCurrency(item.totalPrice)}
          </div>
        )}
      </div>
    )
  }

  // Summary mode (for OrderCard - minimal display)
  if (mode === 'summary') {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          fontSize: '12px',
          color: theme.colors.textSecondary,
          ...style
        }}
      >
        {showQuantity && <span>{item.quantity}x</span>}
        <span>{item.catalogItem.name}</span>
        {isLegacyProduct && (
          <Badge variant="warning" style={{ fontSize: '9px' }}>Legacy</Badge>
        )}
      </div>
    )
  }

  // Editable mode (for CreateOrder - with quantity input and remove button)
  if (mode === 'editable') {
    return (
      <div key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '60px 70px 1fr 100px 120px 100px 60px',
          gap: '16px',
          padding: '12px 16px',
          alignItems: 'center'
        }}>
          {/* Quantity Input */}
          <div style={{ textAlign: 'center' }}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={quantityInput.displayValue}
              onChange={quantityInput.handleChange}
              onFocus={quantityInput.handleFocus}
              onBlur={quantityInput.handleBlur}
              onKeyDown={onKeyDown}
              ref={(el) => setQuantityInputRef?.(item.id, el)}
              style={{
                textAlign: 'center',
                width: '48px',
                height: '32px',
                fontSize: '14px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.sm,
                padding: '4px',
                backgroundColor: theme.colors.surface
              }}
            />
          </div>
          
          {/* Product Image */}
          <div style={{ textAlign: 'center' }}>
            <ProductImage size={50} />
          </div>
          
          {/* Product Info */}
          <div>
            <div style={{ fontWeight: '500', color: '#111827', fontSize: '14px' }}>
              {item.catalogItem.name}
              {isLegacyProduct && (
                <Badge variant="warning" style={{ 
                  marginLeft: '8px',
                  fontSize: '10px'
                }}>
                  LEGACY
                </Badge>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {enhancedProduct?.qtyPerUnit || item.catalogItem.qtyPerUnit} per {enhancedProduct?.unit || item.catalogItem.unit} • 
              {formatCurrency(enhancedProduct?.perItem || item.catalogItem.perItem)}/item • 
              {enhancedProduct?.source || 'API'}
              {enhancedProduct?.orders && enhancedProduct.orders > 0 && ` • Ordered ${enhancedProduct.orders} times`}
            </div>
          </div>
          
          {/* Unit Info */}
          <div style={{ textAlign: 'center', fontSize: '14px', color: '#374151' }}>
            {enhancedProduct?.unit || item.catalogItem.unit}
            {(enhancedProduct?.qtyPerUnit || item.catalogItem.qtyPerUnit) > 1 && (
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                ({enhancedProduct?.qtyPerUnit || item.catalogItem.qtyPerUnit} per {enhancedProduct?.unit || item.catalogItem.unit})
              </div>
            )}
          </div>
          
          {/* Price per Unit */}
          <div style={{ textAlign: 'right', fontSize: '14px', color: '#374151' }}>
            <div style={{ fontWeight: '500' }}>
              {formatCurrency(item.catalogItem.pricePerUnit)}
            </div>
          </div>
          
          {/* Total Price */}
          <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
            {formatCurrency(item.totalPrice)}
          </div>
          
          {/* Remove Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => onRemoveItem?.(item.id)}
              style={{
                width: '32px',
                height: '32px',
                padding: '0',
                fontSize: '18px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.sm,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
} 